import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const DISPLAY_SESSION_KEY = 'taginn_display_session';
const HEARTBEAT_INTERVAL = 30000; // 30 seconds

export interface DisplaySession {
  displayId: string;
  sessionToken: string;
  businessId: string;
  displayName: string;
}

export interface ContentItem {
  id: string;
  content_type: 'image' | 'video' | 'html' | 'url' | 'dashboard';
  storage_path?: string;
  external_url?: string;
  html_content?: string;
  dashboard_type?: string;
  dashboard_config?: Record<string, unknown>;
  duration_seconds: number;
}

export interface PlaylistItem {
  content_item: ContentItem;
  position: number;
  duration_override?: number;
}

export interface Playlist {
  id: string;
  name: string;
  loop_mode: 'continuous' | 'once' | 'shuffle';
  transition_type: 'fade' | 'slide' | 'zoom' | 'none';
  transition_duration_ms: number;
  items: PlaylistItem[];
}

interface PairingCodeResponse {
  success: boolean;
  display_id?: string;
  pairing_code?: string;
  expires_at?: string;
  error?: string;
}

interface HeartbeatResponse {
  success: boolean;
  display_status?: 'active' | 'inactive';
  content_type?: 'playlist' | 'content_item' | 'none';
  playlist?: Playlist;
  content_item?: ContentItem;
  next_heartbeat_ms?: number;
  error?: string;
}

export function useDisplaySession() {
  const [session, setSession] = useState<DisplaySession | null>(null);
  const [isPaired, setIsPaired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingCodeExpiresAt, setPairingCodeExpiresAt] = useState<Date | null>(null);
  const [displayId, setDisplayId] = useState<string | null>(null);
  const [currentPlaylist, setCurrentPlaylist] = useState<Playlist | null>(null);
  const [currentContent, setCurrentContent] = useState<ContentItem | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Load session from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(DISPLAY_SESSION_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as DisplaySession;
        setSession(parsed);
        setIsPaired(true);
        setDisplayId(parsed.displayId);
      } catch {
        localStorage.removeItem(DISPLAY_SESSION_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  // Start heartbeat when paired
  useEffect(() => {
    if (isPaired && session) {
      startHeartbeat();
      subscribeToUpdates();
    }

    return () => {
      stopHeartbeat();
      unsubscribeFromUpdates();
    };
  }, [isPaired, session]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) return;

    const sendHeartbeat = async () => {
      if (!session) return;

      try {
        const response = await supabase.functions.invoke<HeartbeatResponse>('signage-heartbeat', {
          body: {
            display_id: session.displayId,
            session_token: session.sessionToken,
            current_content_id: currentContent?.id || currentPlaylist?.items[0]?.content_item.id,
          },
        });

        if (response.error) {
          console.error('Heartbeat error:', response.error);
          return;
        }

        const data = response.data;
        if (!data?.success) {
          if (data?.error === 'Invalid or expired session') {
            // Session expired, clear and show pairing screen
            clearSession();
          }
          return;
        }

        setIsActive(data.display_status === 'active');

        if (data.content_type === 'playlist' && data.playlist) {
          setCurrentPlaylist(data.playlist);
          setCurrentContent(null);
        } else if (data.content_type === 'content_item' && data.content_item) {
          setCurrentContent(data.content_item);
          setCurrentPlaylist(null);
        } else {
          setCurrentPlaylist(null);
          setCurrentContent(null);
        }
      } catch (err) {
        console.error('Heartbeat failed:', err);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Set up interval
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
  }, [session, currentContent, currentPlaylist]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const subscribeToUpdates = useCallback(() => {
    if (!session || channelRef.current) return;

    const channel = supabase.channel(`signage:display:${session.displayId}`)
      .on('broadcast', { event: 'content_update' }, (payload) => {
        const data = payload.payload;
        if (data.type === 'playlist') {
          setCurrentPlaylist(data.playlist);
          setCurrentContent(null);
        } else if (data.type === 'content_item') {
          setCurrentContent(data.content_item);
          setCurrentPlaylist(null);
        }
      })
      .on('broadcast', { event: 'reload' }, () => {
        window.location.reload();
      })
      .on('broadcast', { event: 'unpair' }, () => {
        clearSession();
      })
      .subscribe();

    channelRef.current = channel;
  }, [session]);

  const unsubscribeFromUpdates = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  // Request a new pairing code
  const requestPairingCode = useCallback(async () => {
    setError(null);

    try {
      const deviceInfo = {
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        device_type: detectDeviceType(),
      };

      const response = await supabase.functions.invoke<PairingCodeResponse>('signage-generate-pairing-code', {
        body: { device_info: deviceInfo },
      });

      if (response.error) {
        setError('Failed to generate pairing code');
        return;
      }

      const data = response.data;
      if (!data?.success || !data.pairing_code) {
        setError(data?.error || 'Failed to generate pairing code');
        return;
      }

      setPairingCode(data.pairing_code);
      setPairingCodeExpiresAt(data.expires_at ? new Date(data.expires_at) : null);
      setDisplayId(data.display_id || null);

      // Subscribe to pairing channel to receive completion notification
      if (data.display_id) {
        const pairingChannel = supabase.channel(`signage:pairing:${data.display_id}`)
          .on('broadcast', { event: 'paired' }, (payload) => {
            const pairingData = payload.payload;
            completePairing({
              displayId: pairingData.display_id,
              sessionToken: pairingData.session_token,
              businessId: pairingData.business_id,
              displayName: pairingData.display_name,
            });
            supabase.removeChannel(pairingChannel);
          })
          .subscribe();
      }
    } catch (err) {
      console.error('Failed to request pairing code:', err);
      setError('Failed to connect to server');
    }
  }, []);

  // Complete pairing (called when pairing is confirmed)
  const completePairing = useCallback((newSession: DisplaySession) => {
    localStorage.setItem(DISPLAY_SESSION_KEY, JSON.stringify(newSession));
    setSession(newSession);
    setIsPaired(true);
    setPairingCode(null);
    setPairingCodeExpiresAt(null);
    setDisplayId(newSession.displayId);
    setError(null);
  }, []);

  // Clear session (unpair)
  const clearSession = useCallback(() => {
    localStorage.removeItem(DISPLAY_SESSION_KEY);
    setSession(null);
    setIsPaired(false);
    setCurrentPlaylist(null);
    setCurrentContent(null);
    setPairingCode(null);
    setPairingCodeExpiresAt(null);
    setDisplayId(null);
    stopHeartbeat();
    unsubscribeFromUpdates();
  }, [stopHeartbeat, unsubscribeFromUpdates]);

  return {
    session,
    isPaired,
    isLoading,
    isActive,
    pairingCode,
    pairingCodeExpiresAt,
    displayId,
    currentPlaylist,
    currentContent,
    error,
    requestPairingCode,
    completePairing,
    clearSession,
  };
}

function detectDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('tizen')) return 'smart_tv';
  if (ua.includes('webos')) return 'smart_tv';
  if (ua.includes('android tv')) return 'smart_tv';
  if (ua.includes('fire tv') || ua.includes('silk')) return 'fire_stick';
  if (ua.includes('crkey') || ua.includes('chromecast')) return 'chromecast';
  if (ua.includes('android')) return 'android_box';
  if (ua.includes('raspberry')) return 'raspberry_pi';

  return 'browser';
}
